import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AuthConfigResult {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
  config?: {
    site_url: string;
    redirect_urls: string[];
    origin: string;
  };
}

export const useAuthStartupConfig = () => {
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [configResult, setConfigResult] = useState<AuthConfigResult | null>(null);

  useEffect(() => {
    const configureAuthSettings = async () => {
      // Only run once per session
      if (sessionStorage.getItem('auth-config-completed')) {
        return;
      }

      setIsConfiguring(true);
      
      try {
        console.log('Starting auth configuration...', {
          timestamp: new Date().toISOString(),
          origin: window.location.origin,
          hostname: window.location.hostname
        });
        
        const { data, error } = await supabase.functions.invoke('auth-config-startup', {
          method: 'POST',
          body: {}
        });

        if (error) {
          console.error('Auth configuration edge function error:', {
            error: error.message,
            context: error.context,
            timestamp: new Date().toISOString()
          });
          
          // Determine user-friendly error message
          let userMessage = 'Failed to configure authentication settings';
          if (error.message.includes('Function not found')) {
            userMessage = 'Authentication service temporarily unavailable';
          } else if (error.message.includes('timeout')) {
            userMessage = 'Configuration request timed out - will retry automatically';
          } else if (error.message.includes('network')) {
            userMessage = 'Network error during configuration';
          }
          
          setConfigResult({
            success: false,
            error: userMessage,
            code: 'EDGE_FUNCTION_ERROR'
          });
        } else {
          console.log('Auth configuration response:', {
            data,
            timestamp: new Date().toISOString()
          });
          
          setConfigResult(data);
          
          // Mark as completed for this session only if successful
          if (data?.success) {
            sessionStorage.setItem('auth-config-completed', 'true');
            
            // Optional success notification (can be disabled for production)
            if (process.env.NODE_ENV === 'development') {
              toast.success('Auth Configuration', {
                description: 'Authentication settings configured successfully'
              });
            }
          } else {
            // Handle server-side errors with user-friendly messages
            const errorCode = data?.code || 'UNKNOWN';
            const errorMessage = data?.error || 'Configuration failed';
            
            console.warn('Auth configuration failed:', {
              code: errorCode,
              error: errorMessage,
              details: data?.details,
              timestamp: new Date().toISOString()
            });
            
            // Don't show error toast for non-critical failures
            if (errorCode !== 'CONFIG_FAILED') {
              toast.error('Configuration Issue', {
                description: errorMessage
              });
            }
          }
        }
      } catch (error: any) {
        console.error('Critical auth configuration error:', {
          error: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        });
        
        setConfigResult({
          success: false,
          error: 'Unexpected error during authentication setup',
          code: 'CLIENT_ERROR'
        });
        
        // Only show toast for critical errors
        toast.error('Setup Error', {
          description: 'Failed to initialize authentication. Please refresh the page.'
        });
      } finally {
        setIsConfiguring(false);
      }
    };

    // Run configuration on app startup
    configureAuthSettings();
  }, []);

  return {
    isConfiguring,
    configResult,
    isConfigured: configResult?.success || false
  };
};